import { getInitialMatrixData } from '@/components/data/entry/items/MatrixHelper';
import type { BlobModel } from '@/models/blob';
import type { EntryContext, PostEntry } from '@/models/entry';
import type { Part11Signature, Part11SignatureCheck, SignatureRefusal } from '@/models/part11';
import {
  FormFieldType,
  FormModel,
  type FormField,
  FormFieldChoice
} from '@/models/form';
import { LoadingStateModel } from '@/models/loadingState';
import { buildEntryPayload } from '@/models/entryPayload';
import { isAnswered } from '@/models/fieldAnswer';
import { matrixRuleViolation } from '@/models/matrixRules';
import BlobService from '@/services/blobService';
import EntryService from '@/services/entryService';
import FormService from '@/services/formService';
import MetricsService from '@/services/metricsService';
import { defineStore } from 'pinia';
import { computed, reactive, ref, shallowRef, toRefs, watch } from 'vue';
import { useRoute } from 'vue-router';
import { createEntryIdentifier, singleQueryValue } from '@/utils';
import {
  computeEffectProps,
  getAssignDependencyIdentifiers,
  isReadOnlyByEffects,
  resetCalculatedValue
} from '@/components/data/entry/effects';
import { useStudyStore } from './study';
import type { SelectChangeEvent } from 'primevue/select';
import { usePipelineStore } from './pipeline';
import caseService from '@/services/caseService';
import { cloneDeep } from 'lodash';
import { useEntryListStore } from './entryList';
import { AxiosError } from 'axios';
import { uuid } from 'vue-uuid';
import { MetricsEvent } from '@/utils/metricsEvent';
import { createRequestToken } from './requestToken';
import { createFileUploads, type FileSlot, type FileUploads } from '@/components/data/entry/files/fileUploads';
import type { FieldChangeOptions } from '@/components/data/entry/items/fieldItem';
import { syncFileAnswers } from '@/components/data/entry/items/syncFileAnswers';
import { createMemoryUploader } from '@/components/data/entry/files/memoryUploader';
import { readerChanges } from '@/components/data/entry/saveConflict';
import { createS3Uploader } from '@/components/data/entry/files/s3Uploader';
import {
  freshEntryState,
  openEntry as openEntryWith,
  saveOutcomeOf,
  type AnswersFrom,
  type EntryOpenRequest,
  type EntryState,
  type OpenResult,
  type SaveOutcome
} from './entryOpen';

type Translator = (key: string) => string;

let translator: Translator | undefined;

/**
 * Hands the store `i18n.global.t` for the copy it draws itself: the save toasts and the tooltip on a
 * form the user may not edit. `main.ts` calls it next to `setErrorTranslator`, before anything
 * mounts, so a store that has none is a test that did not set one, and it reads the bare key.
 */
export const setEntryTranslator = (translate: Translator): void => {
  translator = translate;
};

const translate = (key: string): string => translator?.(key) ?? key;

export const useEntryStore = defineStore('entry', () => {

  const route = useRoute();
  const studyStore = useStudyStore();
  const pipelineStore = usePipelineStore();
  const entryListStore = useEntryListStore();

  /**
   * The entry on screen, one object that `openEntry` assigns whole (ENG-9073 decision 13). Each key
   * is handed out as a ref of its own, so a caller reads `entryStore.form` as it always has.
   */
  const state = reactive(freshEntryState()) as EntryState;
  const {
    form,
    initialForm,
    entry,
    entryLocked,
    entryActivities,
    entryActivitiesFailed,
    selectedLanguage,
    effectsSettled,
    entryTokenPreview,
    anonymousEntrySubmitError,
    openOutcome,
    formClosedMessage,
    anonymousResponsesNotAllowed,
    oldFormItems,
    restoredEntryData,
    touchedFields,
    changedFields,
    saveConflict
  } = toRefs(state);

  // The route record says so, not the path: see `publicEntry` in src/router/index.ts.
  const isAnonymousEntry = computed(() => route.meta?.publicEntry === true);
  /**
   * The form preview, opened from the forms list or the builder (`meta.entryPreview`). It runs the
   * fields, effects and validation, and writes nothing: no entry, no upload, no metrics, no draft.
   * Its values live here for the tab (docs/forms/REVIEW.md F7, decisions 132, 138 and 139).
   */
  const isFormPreview = computed(() => route.meta?.entryPreview === true);
  const multipleEntriesError = computed(() => form.value?.multipleEntriesError);
  let restoredDataTimeoutId: ReturnType<typeof setTimeout> | undefined;

  /**
   * Set by hosts that render the entry outside its own route (the pipeline's entry dialog), where the form and entry
   * identifiers live in the query rather than in route params. When unset, the route is the source of truth.
   */
  const contextOverride = ref<EntryContext>();

  const entryParams = computed<EntryContext>(() => contextOverride.value ?? {
    formIdentifier: route.params?.formIdentifier as string,
    entryIdentifier: route.params?.entryIdentifier as string | undefined,
    caseIdentifier: (route.params?.identifier ?? route.query?.cid) as string | undefined,
    token: route.query?.token as string | undefined,
    copyFrom: singleQueryValue(route.query?.copyFrom)
  });

  const setEntryContext = (context: EntryContext) => {
    contextOverride.value = context;
  };

  const clearEntryContext = () => {
    contextOverride.value = undefined;
  };

  const caseId = computed(() => entryParams.value.caseIdentifier);
  const entryToken = computed(() => entryParams.value.token);

  /**
   * The case whose details are loaded and shown alongside the entry. Anonymous entries carry a `cid` for attribution
   * only and must not fetch case details, which they have no permission to read — hence the narrower source than
   * `caseId`.
   */
  const selectedCaseIdentifier = computed(() => contextOverride.value ?
    contextOverride.value.caseIdentifier :
    route.params?.identifier as string | undefined);

  const getFormLoader = ref(new LoadingStateModel({ isLoading: false }));
  /** The open ended closed, which `Entry.vue` draws as the closed screen. A gone token draws it too, with its own line. */
  const formClosedError = computed(() => openOutcome.value === 'closed' || openOutcome.value === 'tokenGone');
  /**
   * The API refused the entry read, by id or by token, or the case read, and the open with it; a
   * 404 draws the same (ENG-9073 decisions 3 and 4). `Entry.vue` draws ADR 0010's refused screen for
   * it, with no form, Edit or Save. A public form that stopped taking answers is refused too, and
   * draws the same screen in its own words through `anonymousResponsesNotAllowed`.
   */
  const entryReadRefused = computed(() =>
    (openOutcome.value === 'refused' || openOutcome.value === 'notFound') && !anonymousResponsesNotAllowed.value);

  /**
   * The list the reader came into the entry routes from, which a create on a dashboard route goes back to (ENG-9073
   * decision 17). `undefined` when they came from anywhere else, a deep link or a new tab included, and the create
   * goes to DATA. The route guard sets it through `recordStartedFrom` as the reader comes in.
   */
  const startedFrom = ref<string>();
  const recordStartedFrom = (list: string | undefined) => {
    startedFrom.value = list;
  };

  /**
   * One reader's time on one entry (CONTEXT.md's Entry session). It outlives the re-read after a
   * save, and opening another entry closes it. A preview never has one, and every metrics call
   * below waits on one, so none of them goes out for a preview.
   */
  interface EntrySession {
    id: string;
    /** What the session was opened on, so opening the same saved entry again starts nothing. */
    formIdentifier: string;
    openedOn?: string;
    /** The saved entry the session is on, for its CLOSE: a create or a public edit link sets it. */
    entryIdentifier: string;
    caseIdentifier?: string;
    token?: string;
    /** Set when OPEN goes out, from the form it read. CLOSE goes out only for a session that sent OPEN. */
    studyId?: string;
    formId?: string;
    startTracked: boolean;
    /** The API does not dedupe a CLOSE, so a session sends one at most. */
    closed: boolean;
    removeListeners: () => void;
  }

  const session = ref<EntrySession>();
  const entrySessionId = computed(() => session.value && !session.value.closed ? session.value.id : null);
  const openSession = () => session.value && !session.value.closed ? session.value : undefined;

  /** What the session was opened on: a saved entry by id or by its public edit link. A new entry is none. */
  const sessionKey = (request: EntryOpenRequest) =>
    request.entryIdentifier ?? (request.token ? `token:${request.token}` : undefined);

  const sessionEvent = (current: EntrySession, event: MetricsEvent) => ({
    entryId: current.entryIdentifier,
    sessionId: current.id,
    studyId: current.studyId ?? '',
    formId: current.formId ?? '',
    event,
    timestamp: Date.now(),
    entryToken: current.token,
    caseId: current.caseIdentifier
  });

  /**
   * Ends the entry session: sends its CLOSE and removes its unload and visibility listeners, and
   * nothing more (ENG-9073 decision 12). `beforeunload`, the route guard on leaving the entry routes
   * and `EntryDialog`'s unmount call it; opening another entry calls it through `openEntry`.
   */
  const closeEntrySession = () => {
    const current = openSession();
    if (!current) {
      return;
    }
    current.closed = true;
    current.removeListeners();
    if (current.formId) {
      MetricsService.recordEntryMetricsWithKeepAlive(sessionEvent(current, MetricsEvent.FORM_ENTRY_CLOSE));
    }
  };

  const listenForSessionEnd = (current: EntrySession) => {
    const handleBeforeUnload = () => {
      closeEntrySession();
    };

    const handleVisibilityChange = () => {
      if (current.closed || !current.formId) {
        return;
      }
      const event = document.hidden ? MetricsEvent.FORM_ENTRY_HIDDEN : MetricsEvent.FORM_ENTRY_VISIBLE;
      MetricsService.recordEntryMetrics(sessionEvent(current, event)).catch(() => { });
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  };

  const sessions = {
    holds: (request: EntryOpenRequest) => {
      const current = openSession();
      const key = sessionKey(request);
      return !!current && key !== undefined &&
        current.formIdentifier === request.formIdentifier && current.openedOn === key;
    },
    close: closeEntrySession,
    start: (request: EntryOpenRequest) => {
      const started: EntrySession = {
        id: uuid.v4(),
        formIdentifier: request.formIdentifier,
        openedOn: sessionKey(request),
        entryIdentifier: request.entryIdentifier ?? '',
        caseIdentifier: request.caseIdentifier,
        token: request.token,
        startTracked: false,
        closed: false,
        removeListeners: () => { }
      };
      started.removeListeners = listenForSessionEnd(started);
      session.value = started;
    },
    recordEntry: (entryIdentifier: string) => {
      const current = openSession();
      if (current) {
        current.entryIdentifier = entryIdentifier;
        // A new entry's session is on the entry it created from here. A public edit link's stays on
        // its token, which is what the re-read after a save asks for.
        current.openedOn ??= entryIdentifier;
      }
    }
  };

  /** Sends the session's OPEN, once: the re-read after a save opens the entry the session holds. */
  const recordSessionOpen = () => {
    const current = openSession();
    if (!current || current.formId || !form.value?.identifier) {
      return;
    }
    current.formId = form.value.identifier;
    current.studyId = studyStore.selectedStudyId || form.value.studyId || '';
    MetricsService.recordEntryMetrics({ ...sessionEvent(current, MetricsEvent.FORM_ENTRY_OPEN), entryId: '' })
      .catch(() => { });
  };

  /** One counter for every open: the newest ask owns the screen, and an older reply is dropped. */
  const openRequests = createRequestToken();

  /** Fills a new entry's answers with blanks, then puts back a public entry's cached draft. */
  const startNewEntry = (): 'blank' | 'draft' => {
    setInitialValuesForNewEntry();

    // A preview never saved one to restore.
    if (!form.value?.cachePublicEntry || isFormPreview.value) {
      return 'blank';
    }
    const cachedEntryData = localStorage.getItem(`publicEntry_${form.value?.identifier}`);
    if (!cachedEntryData) {
      return 'blank';
    }
    restoredEntryData.value = true;
    restoredDataTimeoutId = setTimeout(() => {
      restoredEntryData.value = false;
    }, 60000);
    try {
      const parsedData = JSON.parse(cachedEntryData);
      if (parsedData && parsedData.items) {
        form.value!.items = parsedData.items;
        return 'draft';
      }
    } catch (e) {
      // Clear invalid data
      // eslint-disable-next-line no-console
      console.error('Failed to restore cached public entry data', {
        formIdentifier: form.value?.identifier,
        error: e
      });
    }
    return 'blank';
  };

  /**
   * The open whose conditions pass hasn't run yet. Until it does, what the effects write belongs to the entry as it
   * opened, so the watch on `form.items` copies it into `initialForm` as the pass does. Otherwise the answers a Show
   * effect resets read as unsaved changes: a save resolves in this gap, after its re-read, and the entry dialog closes
   * on it through the route guard's unsaved-changes check.
   */
  let pendingOpenEffects: Promise<void> | undefined;

  const finishOpen = (stillCurrent: () => boolean, answersFrom: AnswersFrom) => {
    void getFilesMetadata();
    initialForm.value = cloneDeep(form.value);
    const settled: Promise<void> = new Promise<void>(resolve => {
      /** We're using `setTimeout` to ensure all fields are initially visible, allowing the Virtualizer to allocate space in the view.
       * After that, `computeEffects` runs, and the conditions are applied. */
      setTimeout(() => {
        if (pendingOpenEffects === settled) {
          pendingOpenEffects = undefined;
        }
        if (stillCurrent()) {
          computeEffects();
          initialForm.value = cloneDeep(form.value);
        }
        resolve();
      }, 10);
    });
    pendingOpenEffects = settled;
    effectsSettled.value = settled;

    //set form name
    document.title = `${form.value?.title} | Studypages Data`;

    recordSessionOpen();

    // A copy's answers are someone's answers, and so are a draft's, which a default must not overwrite (ADR 0012).
    if (answersFrom === 'blank') {
      setDefaltValues();
    }
  };

  /**
   * The open entry's files, one instance per open (ENG-9088 decision 11). The file field and the
   * matrix file cell draw their slots from it and report each slot's answer through `change()`
   * (ENG-9090, ENG-9091).
   */
  const fileUploads = shallowRef<FileUploads>();

  /**
   * Aborts the last entry's uploads and makes the next entry its own instance, so an upload still
   * in flight can never write into the entry opened after it. Not in `freshEntryState()`, because
   * the old instance has to be stopped before the reset drops it.
   */
  const replaceFileUploads = (formIdentifier: string) => {
    fileUploads.value?.abortAll();
    fileUploads.value = createFileUploads({
      uploader: isFormPreview.value ? createMemoryUploader() : createS3Uploader(formIdentifier)
    });
  };

  /**
   * Opens the entry the route or the dialog's context names, through `openEntry` in `entryOpen.ts`:
   * it closes the previous entry session, resets the state above, claims a token, then reads.
   * Opening the saved entry the session already holds re-reads it and starts nothing, which is how a
   * save refreshes the entry it saved, and it keeps the saved form on screen until the re-read lands.
   */
  const openEntry = async (): Promise<OpenResult> => {
    hideRestoredEntryDataMsg();
    const { formIdentifier, entryIdentifier, caseIdentifier, token, copyFrom } = entryParams.value;
    replaceFileUploads(formIdentifier);
    return openEntryWith({
      services: {
        // The URL's study first: a public study link carries one, and nothing selects it there.
        readForm: request => FormService.getForm(
          request.formIdentifier, request.caseIdentifier, request.studyIdentifier, request.token),
        readEntry: (identifier, readForm) => EntryService.getEntry(identifier, readForm),
        readEntryByToken: (linkToken, readForm) => EntryService.getEntryByToken(linkToken, readForm),
        readCase: async identifier =>
          pipelineStore.caseList.find(caseItem => caseItem.identifier == identifier) ??
          (entryListStore.selectedCase?.identifier === identifier ? entryListStore.selectedCase : undefined) ??
          /** Neither store holds the case when the entry was deep-linked, or when the case sits on another page of the
           * pipeline — fetch it so the header and the case notes still have something to show. */
          await caseService.getCaseEntries(identifier)
      },
      state,
      requests: openRequests,
      loader: getFormLoader.value,
      sessions,
      startNewEntry,
      finishOpen
    }, {
      formIdentifier,
      entryIdentifier,
      caseIdentifier,
      selectedCaseIdentifier: selectedCaseIdentifier.value,
      studyIdentifier: (route.params?.studyIdentifier as string | undefined) ?? studyStore.selectedStudyId,
      token,
      preview: isFormPreview.value,
      publicEntry: isAnonymousEntry.value,
      copyFrom
    });
  };

  const resetRestoredEntryData = () => {
    form.value?.items?.forEach(field => {
      field.value = undefined;
      field.numberValue = undefined;
      if (field.type === FormFieldType.CHOICE) {
        field.options.choices?.forEach(choice => choice.checked = false);
        field.choiceValue = FormFieldChoice.createEmpty();
        field.otherChoiceValue = undefined;
      }
      if (field.type === FormFieldType.MATRIX) {
        field.matrixValues = [];
        field.matrixTableRowValues = getInitialMatrixData(field as FormField);
      }
    });
    restoredEntryData.value = false;
    if (restoredDataTimeoutId) {
      clearTimeout(restoredDataTimeoutId);
    }
  };

  const hideRestoredEntryDataMsg = () => {
    restoredEntryData.value = false;
    if (restoredDataTimeoutId) {
      clearTimeout(restoredDataTimeoutId);
    }
  };


  watch(() => form.value?.items, () => {
    // only compute effects if there has been changes made
    if (
      form.value &&
      JSON.stringify(form.value?.items) !== JSON.stringify(oldFormItems.value) &&
      !getFormLoader.value.isLoading
    ) {
      computeEffects();
      if (pendingOpenEffects) {
        initialForm.value = cloneDeep(form.value);
      }
    }
    // `isEntryPristine` deep-clones and serialises both forms, so skip it once the start is recorded.
    if (!openSession()?.startTracked && !isEntryPristine.value) {
      trackFormStart();
    }
    oldFormItems.value = form.value?.items ? cloneDeep(form.value?.items) : undefined;
  }, { deep: true });

  const trackFormStart = async () => {
    const current = openSession();
    if (current && !current.startTracked && entrySessionId.value) {
      current.startTracked = true;

      const startTimestamp = Date.now();

      if (form.value?.identifier) {
        await MetricsService.recordEntryMetrics({
          entryId: entryParams.value.entryIdentifier || entry.value?.identifier || '',
          sessionId: entrySessionId.value,
          studyId: studyStore.selectedStudyId || form.value.studyId || '',
          formId: form.value.identifier,
          event: MetricsEvent.FORM_ENTRY_START,
          timestamp: startTimestamp,
          entryToken: entryToken.value,
          caseId: caseId.value
        }).catch(() => { });
      }
    }
  };

  /** The other fields each field's `Assign` calculation reads, keyed by field identifier. */
  const getAssignDependencies = (fields: FormField[]) => {
    return new Map(fields.map(field => [
      field.identifier,
      getAssignDependencyIdentifiers(field.conditions).filter(identifier => identifier !== field.identifier)
    ]));
  };

  /**
   * Identifiers of the fields whose value can be considered "changed" for this compute pass: the fields the user has
   * edited, plus the fields that calculate their own value from one of those (so chains of calculated fields keep
   * working even when assigning a value doesn't flag the target field as changed, e.g. dates).
   */
  const getChangedFieldIdentifiers = (fields: FormField[], assignDependencies: Map<string, string[]>) => {
    const changedIdentifiers = new Set(fields
      .filter(field => changedFields.value.has(field.identifier))
      .map(field => field.identifier));

    let resolvedNewDependent = true;
    while (resolvedNewDependent) {
      resolvedNewDependent = false;
      for (const field of fields) {
        if (
          !changedIdentifiers.has(field.identifier) &&
          assignDependencies.get(field.identifier)!.some(identifier => changedIdentifiers.has(identifier))
        ) {
          changedIdentifiers.add(field.identifier);
          resolvedNewDependent = true;
        }
      }
    }

    return changedIdentifiers;
  };

  const computeEffects = () => {
    const fields = form.value?.items || [];
    const assignDependencies = getAssignDependencies(fields);
    const changedIdentifiers = getChangedFieldIdentifiers(fields, assignDependencies);

    fields.forEach(field => {
      /** A calculation that depends on other fields must not run until at least one of them has actually been edited,
       * otherwise opening an entry recalculates (and therefore dirties) it on its own. */
      const dependencies = assignDependencies.get(field.identifier)!;
      const skipAssign = dependencies.length > 0 && !dependencies.some(identifier => changedIdentifiers.has(identifier));

      computeEffectProps(field, fields, { changed: changedFields.value, skipAssign });

      // finding matrix fields that are dependent on other field's value
      if (field.type == FormFieldType.MATRIX) {
        const conditions = field.options.rows?.filter(row => row.dynamicRowConfig?.conditions);
        if (conditions) {
          const matches = JSON.stringify(conditions).match(/"@([^"]+)"/g)?.map(m => m.slice(2, -1)) || [];

          for (const matchedId of matches) {
            const matchedField = form.value?.items?.find(item => item.identifier == matchedId);
            if (matchedField && changedFields.value.has(matchedField.identifier)) {
              field.matrixDependencyResolved = field.matrixDependencyResolved ? field.matrixDependencyResolved + 1 : 1;
            }
          }
        }
      }
    });
  };

  /**
   * The one write a field item makes (ADR 0011): applies `patch` to the store's own field and adds
   * the field to the touched and changed sets, or to changed alone for a write that says `touch: false`.
   * The watch on `form.items` then reruns the effects. `clearDependents` empties the fields that name this
   * one without adding them to changed, as a date's Reset always has: the effects run again because this
   * field is in changed.
   */
  const changeField = (
    identifier: string,
    patch: Partial<FormField>,
    { touch = true, clearDependents = false }: FieldChangeOptions = {}
  ) => {
    const fields = form.value?.items;
    const field = fields?.find(item => item.identifier === identifier);
    if (!field) {
      return;
    }
    Object.assign(field, patch);
    // A matrix Save flagged is checked again on every change, so its message follows the reader's fix. A matrix Save
    // never flagged waits for Save, which is when the reader asked for the answers to be checked.
    if (field.invalidInput) {
      field.invalidInput = matrixRuleViolation(field);
    }
    if (touch) {
      touchedFields.value.add(identifier);
    }
    changedFields.value.add(identifier);
    if (clearDependents) {
      resetCalculatedValue(field, fields!);
    }
  };

  /**
   * Whether the error display treats `field` as touched: the reader changed it through its control,
   * or Save counted it.
   */
  const isFieldTouched = (field: FormField): boolean => touchedFields.value.has(field.identifier);

  /**
   * Seeds the open module's file field and matrix file cell slots with the stored files their
   * answers name, and reads their metadata. A read that answers after the module was replaced, by a
   * later open or a discard, is dropped by the module, rather than drawn in the next entry.
   */
  const getFilesMetadata = async () => {
    await fileUploads.value?.readStored(form.value?.items ?? []);
  };

  /** Set while Save waits for the files still queued or uploading, which both Saves read for their label and lock. */
  const waitingForUploads = ref(false);

  /**
   * Save's first step (ENG-9088 decision 4): waits until no file is queued or uploading, then writes every slot's stored
   * files into its answer, so the checks and the save that follow read the files that made it. A failed file stays out
   * of the answer, and the caller asks about it. With nothing to wait for it writes at once and returns `undefined`, so
   * a Save with no upload running goes on in the same turn, as it did before there was a wait. When the entry is
   * discarded or another one opens during the wait, it writes nothing, since the files belong to an entry gone from
   * the screen.
   */
  const waitForUploads = (): Promise<void> | undefined => {
    const uploads = fileUploads.value;
    if (!uploads) {
      return undefined;
    }
    // Through the store's one write, so a file that lands in an answer is touched and changed like any other answer.
    const syncAnswers = () => syncFileAnswers(uploads, form.value?.items ?? [], changeField);
    if (uploads.pending.value.length === 0) {
      syncAnswers();
      return undefined;
    }
    waitingForUploads.value = true;
    // `settled()` only resolves, `abortAll()` included, so the wait always ends and clears the flag.
    return uploads.settled().then(() => {
      if (fileUploads.value === uploads) {
        syncAnswers();
      }
    }).finally(() => {
      waitingForUploads.value = false;
    });
  };

  const downloadDisplayCopy = (identifier: string, fileName: string) => {
    BlobService.downloadDisplayCopy(identifier, fileName);
  };

  /**
   * Leaves the preview: drops its values and revokes its object URLs, so the route guard finds
   * nothing unsaved on the way out. The preview never saved anything to ask about.
   */
  const closeFormPreview = () => {
    fileUploads.value?.abortAll();
    fileUploads.value = undefined;
    form.value = undefined;
    initialForm.value = undefined;
  };

  const deleteEffectsVariables = (field: FormField) => {
    delete field.show;
    delete field.warn;
    delete field.error;
    delete field.valid;
    delete field.disable;
    delete field.strictRequired;
    delete field.nonStrictRequired;
    delete field.showRequiredError;
    delete field.matrixDependencyResolved;
    delete field.options.dateShifting;
    delete field.sectionExpanded;
    delete field.expanded;

    field.options.columns?.forEach(column => {
      delete column.options?.showingShiftedDate;
    });

    field.matrixTableRowValues?.forEach(row => {
      row.columnValues?.forEach(column => {
        delete column.unShiftedDate;
      });
    });
  };

  /**
   * Whether the entry holds nothing unsaved: its answers are the ones it opened with, and no file is
   * queued, uploading or failed. A file that has not stored is in no answer, so it is checked here, and
   * the discard prompt asks about it (ENG-9088 decision 12). With no form there is nothing to ask about,
   * so a create, which clears the form, leaves a failed file behind without making the entry dirty.
   */
  const isEntryPristine = computed(() => {
    const uploads = fileUploads.value;
    if (form.value && uploads && (uploads.pending.value.length > 0 || uploads.failed.value.length > 0)) {
      return false;
    }
    const oldForm: FormModel | undefined = cloneDeep(initialForm.value);
    oldForm?.items?.forEach(item => deleteEffectsVariables(item));
    delete oldForm?.selectedCase;
    removeMatrixColumnEmptyConditions(oldForm?.items || []);
    const newForm: FormModel | undefined = cloneDeep(form.value);
    newForm?.items?.forEach(item => deleteEffectsVariables(item));
    delete newForm?.selectedCase;
    removeMatrixColumnEmptyConditions(newForm?.items || []);
    if (!oldForm || !newForm) {
      return true;
    }
    return JSON.stringify(oldForm) === JSON.stringify(newForm);
  });

  const removeMatrixColumnEmptyConditions = (fields: FormField[]) => {
    for (const field of fields) {
      if (field.type === FormFieldType.MATRIX && field.matrixTableRowValues) {
        field.matrixTableRowValues.forEach(row => {
          row.columnValues?.forEach(column => {
            if (column.conditions) {
              delete column.conditions;
            }
          });
        });
      }
    }
  };

  const openDiscardChangesAlert = ref(false);
  const resolveFn = ref();
  const rejectFn = ref();

  const showDiscardChangesDialog = async () => {
    openDiscardChangesAlert.value = true;

    return new Promise((resolve, reject) => {
      resolveFn.value = resolve;
      rejectFn.value = reject;
    });
  };

  /** Closes the discard-changes alert, lets the navigation it is holding go ahead or keeps it on the entry, and forgets
   * it. The alert closes on every path: the route guard doesn't wait for a save, so a navigation during one opens the
   * alert again, and the store outlives the entry, so an alert left open draws over the next one. */
  const settleHeldNavigation = (saved: boolean) => {
    openDiscardChangesAlert.value = false;
    (saved ? resolveFn : rejectFn).value?.();
    resolveFn.value = undefined;
    rejectFn.value = undefined;
  };

  const cancelDiscardChangesDialog = () => {
    /** Reject so the caller (the route guard, or the entry dialog) knows to stay put. Clearing `rejectFn` without
     * calling it would leave the pending navigation unresolved forever. */
    settleHeldNavigation(false);
  };

  const discardEntryChanges = () => {
    // The alert closes on the press, and `rejectFn` goes now, so nothing can keep the reader on the entry during the
    // tick before `settleHeldNavigation` below lets the navigation go.
    openDiscardChangesAlert.value = false;
    rejectFn.value = undefined;
    form.value = undefined;
    // The answers go back to the ones the entry opened with, and so does what counts as edited.
    touchedFields.value = new Set();
    changedFields.value = new Set();
    if (session.value) {
      session.value.startTracked = false;
    }
    setTimeout(() => {
      // Adding a timeout since the `form.value = undefined` alone wasn't helping with the reactivity.
      form.value = cloneDeep(initialForm.value);
      // The file fields' and file cells' slots go back to those answers too, from a new module, so a file the reader
      // deleted comes back and one still uploading is aborted, as leaving the entry aborts it (ENG-9088 decision 12).
      replaceFileUploads(entryParams.value.formIdentifier);
      void getFilesMetadata();
      settleHeldNavigation(true);
    }, 1);
  };

  /**
   * Set for the length of `submitEntry`. Every Save reads it, and a call made while it is set sends nothing: on a slow
   * link a second tap would otherwise send a second request, and on a new entry a second `createEntry` makes a second
   * entry (ENG-9113).
   */
  const entrySaving = ref(false);
  /** The save `entrySaving` is set for, so a call made during it can wait on its result. */
  let saveInFlight: Promise<boolean> | undefined;

  /**
   * Set when the last save was an edit that landed with its Part 11 signature unconfirmed, from the
   * `signatureCheck` spdata-api answers with (ENG-9104): a wrong PIN, one the save locked, none set.
   * The server refuses nothing on the PIN until ENG-9110, so the save went through, and `Entry.vue`
   * keeps the signature dialog open with this for another try. A create, or a save the discard alert's
   * navigation waits on, has left the entry, so it gets only the toast.
   */
  const unsignedEdit = ref<SignatureRefusal>();

  /** Resolves to whether the entry was persisted, so hosts that stay mounted (the entry dialog) can tell a failed save
   * from a successful one — errors are reported through the loader and not rethrown. A call made while another save is
   * in flight resolves `false` and leaves what follows a save to the call already running. */
  const submitEntry = async (navigate?: boolean, signature?: Part11Signature): Promise<boolean> => {
    if (saveInFlight) {
      if (navigate) {
        /** The discard-changes alert's Save, for a navigation that arrived during the save: the running save holds the
         * same values, so the navigation follows its result rather than waiting for a resolve that never comes. */
        settleHeldNavigation(await saveInFlight.catch(() => false));
      }
      return false;
    }
    entrySaving.value = true;
    saveInFlight = persistEntry(navigate, signature).then(outcome => outcome === 'saved');
    let saved = false;
    try {
      saved = await saveInFlight;
      return saved;
    } finally {
      /** A save that went through has already let the alert's navigation go. One that was refused, or threw, keeps it
       * on the entry with the error, as Cancel does, where it would otherwise wait for a resolve that never comes. That
       * closes the alert too, in case a navigation during the save opened it again (ENG-9134). */
      if (navigate && !saved) {
        cancelDiscardChangesDialog();
      }
      entrySaving.value = false;
      saveInFlight = undefined;
    }
  };

  const persistEntry = async (navigate?: boolean, signature?: Part11Signature): Promise<SaveOutcome> => {
    unsignedEdit.value = undefined;
    if (isFormPreview.value) {
      // Saved on the client only: the values become the ones view mode shows, and discard returns to. A file the reader
      // deleted goes with the save, as an entry's re-read drops it (ENG-9096).
      initialForm.value = cloneDeep(form.value);
      fileUploads.value?.dropRemoved();
      if (navigate) {
        settleHeldNavigation(true);
      }
      return 'saved';
    }
    let outcome: SaveOutcome = 'saved';
    // Set when the re-read below was superseded: the newer open owns the loader from then on.
    let loaderHandedOn = false;
    anonymousEntrySubmitError.value = '';
    getFormLoader.value.startLoading();
    const formId = form.value?.identifier;
    const { entryIdentifier, caseIdentifier } = entryParams.value;
    const isEdit = Boolean(entryIdentifier || entry.value);

    if (form.value?.identifier && entrySessionId.value) {
      const submissionTimestamp = Date.now();
      await MetricsService.recordEntryMetrics({
        entryId: entryIdentifier || '',
        sessionId: entrySessionId.value,
        studyId: studyStore.selectedStudyId || form.value.studyId || '',
        formId: form.value.identifier,
        event: MetricsEvent.FORM_ENTRY_SUBMIT,
        timestamp: submissionTimestamp,
        entryToken: entryToken.value,
        caseId: caseId.value
      }).catch(() => { });
    }

    const payload: PostEntry = buildEntryPayload(form.value, entry.value, {
      signature,
      entryToken: entryToken.value,
      studyId: studyStore.selectedStudyId || form.value?.studyId,
      version: entry.value?.updatedAt,
      revisionDate: Date.now()
    });

    hideRestoredEntryDataMsg();

    try {
      if (isEdit) {
        const entryId = entry.value!.identifier;
        const saved = await EntryService.updateEntry(entryId, payload);
        reportSaved('entry_update_success', signature, saved?.data?.signatureCheck, !navigate);

        if (form.value?.identifier && entrySessionId.value) {
          await MetricsService.recordEntryMetrics({
            entryId: entryId,
            sessionId: entrySessionId.value,
            studyId: studyStore.selectedStudyId || form.value.studyId || '',
            formId: form.value.identifier,
            event: MetricsEvent.FORM_ENTRY_SAVE,
            timestamp: Date.now(),
            entryToken: entryToken.value,
            caseId: caseId.value
          }).catch(() => { });
        }

        if (navigate) {
          settleHeldNavigation(true);

          form.value = undefined;
          form.value = cloneDeep(initialForm.value);
        }

        // Saving is not opening: this re-reads the entry the session holds, in the same session.
        loaderHandedOn = await openEntry() === 'superseded';
      } else {
        const entryId = createEntryIdentifier(form.value!.identifier);
        payload.uri = `response/${entryId}`;
        if (caseIdentifier) {
          payload.caseUri = `case/${caseIdentifier}`;
        }
        const entryRes = await EntryService.createEntry(payload);
        reportSaved('entry_create_success', signature, entryRes.data?.signatureCheck, false);
        const newEntryIdentifier = entryRes.headers.location.split('/').pop() || '';
        if (form.value?.identifier && entrySessionId.value) {
          await MetricsService.recordEntryMetrics({
            entryId: newEntryIdentifier,
            sessionId: entrySessionId.value,
            studyId: studyStore.selectedStudyId || form.value.studyId || '',
            formId: form.value.identifier,
            event: MetricsEvent.FORM_ENTRY_SAVE,
            timestamp: Date.now(),
            entryToken: entryToken.value,
            caseId: caseId.value
          }).catch(() => { });
        }

        // The visit carries on as the saved entry's, so its CLOSE names the entry it made.
        sessions.recordEntry(newEntryIdentifier);

        form.value = undefined;
        initialForm.value = undefined;
        if (navigate) {
          settleHeldNavigation(true);
        }

        getFormLoader.value.stopLoadingAndFinish();
      }
      localStorage.removeItem(
        `publicEntry_${formId}`
      );
    } catch (error) {
      outcome = saveOutcomeOf(error, isEdit ? 'edit' : 'create');
      if (outcome === 'conflict') {
        // The conflict screen says what happened, so there is no toast, and no public refusal either: that one hides
        // Save, and the reader is here to save again.
        loaderHandedOn = await reloadAfterConflict() === 'superseded';
        return outcome;
      }
      getFormLoader.value.setError(error);
      if (isAnonymousEntry.value) {
        if (error instanceof AxiosError) {
          anonymousEntrySubmitError.value = error.response?.data.message ?? error.response?.data.error ?? (error as Error).message;
        } else {
          anonymousEntrySubmitError.value = (error as Error).message;
        }
      }
    } finally {
      if (!loaderHandedOn) {
        getFormLoader.value.stopLoadingAndFinish();
      }
    }

    return outcome;
  };

  /**
   * The toast for a save that landed. One that carried a Part 11 signature the server did not confirm says
   * it is not signed instead, and where the reader stays on the entry, `unsignedEdit` takes the refusal back
   * to the signature dialog (ENG-9108).
   */
  const reportSaved = (
    successKey: string,
    signature: Part11Signature | undefined,
    check: Part11SignatureCheck | undefined,
    staysOnEntry: boolean
  ) => {
    if (!signature || check?.method !== 'none') {
      getFormLoader.value.setSuccess(translate(successKey));
      return;
    }
    if (staysOnEntry) {
      unsignedEdit.value = check;
    }
    getFormLoader.value.setWarning(
      translate(staysOnEntry ? 'entry_saved_unsigned' : 'entry_saved_unsigned_add_signature'),
      translate('entry_saved_unsigned_title')
    );
  };

  /**
   * A slot's files as the module holds them, removed ones included, for a reader change to name a file by its name
   * rather than its id.
   */
  const slotFiles = (slot: FileSlot): BlobModel[] => (fileUploads.value?.files(slot) ?? []).map(file => file.blob);

  /**
   * Someone saved the entry after the reader opened it, and the API refused the reader's save (ENG-9073 decision 6).
   * Reads what the reader had changed before the server's version replaces it, re-reads the entry in the same session,
   * and lists those changes beside it, for the reader to re-enter by hand. Nothing is merged: a merge would write answers
   * into an audited record the reader never saw.
   */
  const reloadAfterConflict = async (): Promise<OpenResult> => {
    const changes = readerChanges(
      form.value?.items ?? [],
      { changed: changedFields.value, touched: touchedFields.value },
      slotFiles
    );
    const reopened = await openEntry();
    // Kept when the re-read fails too: the open has already reset the form, so the list is all that is left of what
    // the reader typed. A refused or missing entry draws a screen with no form to re-enter into, and a newer open owns
    // the screen and the state.
    if (reopened === 'opened' || reopened === 'failed') {
      saveConflict.value = { changes };
    }
    return reopened;
  };

  /** Fills a blank open's unanswered fields with the form's defaults. */
  const setDefaltValues = () => {
    for (const field of form.value!.items!) {
      if (field.default && !isAnswered(field)) {
        switch (field.type) {
          case FormFieldType.TEXT:
            field.value = field.default;
            break;

          case FormFieldType.CHOICE:
            field.choiceValue = field.options.choices?.find(choice => choice.value == field.default);
            break;

          // Here rather than in the item, which mounts again each time it scrolls back into view (ENG-9068). Marked
          // changed but not touched, so a calculation that reads the slider is worked out on open. Skipped where the
          // slider's effects make it read-only.
          case FormFieldType.SLIDER:
            if (!isReadOnlyByEffects(field)) {
              changeField(field.identifier, { numberValue: Number(field.default) }, { touch: false });
            }
            break;
        }
      }
    }
  };

  const setInitialValuesForNewEntry = () => {
    for (const field of form.value!.items!) {

      switch (field.type) {
        case FormFieldType.TEXT:
          field.value = '';
          break;

        case FormFieldType.NUMBER:
          field.numberValue = null;
          break;

        case FormFieldType.CHOICE:
          if (field.options.maximumChoicesCount == 1) {
            field.choiceValue = FormFieldChoice.createEmpty();
          } else {
            field.options.choices?.forEach(choice => choice.checked = false);
          }
          break;

        case FormFieldType.DROPDOWN:
          if (field.options.maximumChoicesCount == 1) {
            // @ts-expect-error just initialising the null value
            field.choiceValue = null;
          } else {
            field.choiceValue = [];
          }
          break;

        case FormFieldType.MATRIX:
          field.matrixValues = [];

          field.matrixTableRowValues = getInitialMatrixData(field);
          break;

        case FormFieldType.DRILLDOWN:
          field.drillValue = [];
          break;

        case FormFieldType.UNIT:
          // eslint-disable-next-line no-case-declarations
          const defaultUnit = field.options.units?.list.find(unitItem => unitItem.id === field.options.units!.default);
          field.value = defaultUnit?.unit;
          if (field.options.units?.list.length === 1) {
            field.value = field.options.units?.list[0].unit;
          }
          break;

        case FormFieldType.SECTION:
          if (field.options.collapsedInitially) {
            field.sectionExpanded = false;

            const sectionFields = field.options.fields;
            sectionFields?.forEach(fieldItem => {
              const foundField = form.value?.items!.find(item => item.identifier === fieldItem);
              if (foundField) {
                foundField.expanded = field.sectionExpanded;
              }
            });
          }
          break;
        default:
          break;
      }
    }
  };

  const switchLangauge = (event: SelectChangeEvent) => {
    for (const translatedField of event.value.items!) {
      for (const field of form.value!.items!) {
        if (field.identifier === translatedField.identifier) {
          field.label = translatedField.label;
          field.description = translatedField.description;

          const initialFormField = initialForm.value?.items?.find(item => item.identifier == field.identifier);

          initialFormField!.label = field.label;
          initialFormField!.description = field.description;

          if (translatedField.options.choices) {
            for (const translatedChoice of translatedField.options.choices) {
              for (const choice of field.options.choices!) {
                if (choice.value == translatedChoice.value) {
                  choice.label = translatedChoice.label;
                  break;
                }
              }

              for (const choice of initialFormField!.options.choices!) {
                if (choice.value == translatedChoice.value) {
                  choice.label = translatedChoice.label;
                  break;
                }
              }
            }
          }

          // manage matrix field translations
          if (translatedField.options.columns) {
            for (const translatedColumn of translatedField.options.columns) {
              for (const column of field.options.columns!) {
                if (column.id == translatedColumn.id) {
                  column.label = translatedColumn.label;
                  break;
                }
              }
            }

            for (const translatedColumn of translatedField.options.columns) {
              for (const column of initialFormField!.options.columns!) {
                if (column.id == translatedColumn.id) {
                  column.label = translatedColumn.label;
                  break;
                }
              }
            }
          }

          if (translatedField.options.rows) {
            for (const translatedRow of translatedField.options.rows) {
              for (const tableRow of field.matrixTableRowValues!) {
                if (`${translatedRow.id}` === tableRow.rowId) {
                  tableRow.rowHeader = translatedRow.label;
                  break;
                }

              }
            }

            for (const translatedRow of translatedField.options.rows) {
              for (const tableRow of initialFormField!.matrixTableRowValues!) {
                if (`${translatedRow.id}` === tableRow.rowId) {
                  tableRow.rowHeader = translatedRow.label;
                  break;
                }

              }
            }
          }

          break;
        }

      }
    }
  };

  /** A field a condition hides or that is archived is not drawn, so Save's checks neither flag it nor count it. */
  const isShownToSave = (field: FormField) => field.show !== false && !field.archivedAt;

  /**
   * Flags every shown required and soft-required field left unanswered, clears the flag on the
   * answered ones, and returns the ids of the required fields blocking Save in the order the form
   * draws them. Save reveals the first and announces how many there are. A soft-required field is
   * flagged but never blocks, and a field a condition hides or that is archived is not shown, so it
   * neither blocks nor is flagged (ENG-9081 decisions 1, 2 and 11). Both branches ask `isAnswered`,
   * as the live required badge does (ENG-9018 decisions 2 and 11). A flagged field is touched, so
   * its banner shows, and not changed: Save writes no answer (ENG-9040 decision 2).
   */
  const lookForRequiredFields = (): string[] => {
    const blocking: string[] = [];
    form.value?.items?.forEach(field => {
      if (!isShownToSave(field)) {
        return;
      }

      if (field.strictRequired) {
        if (!isAnswered(field)) {
          field.showRequiredError = true;
          touchedFields.value.add(field.identifier);
          blocking.push(field.identifier);
        } else {
          field.showRequiredError = false;
        }
      }

      if (field.nonStrictRequired) {
        if (!isAnswered(field)) {
          field.showRequiredError = true;
          touchedFields.value.add(field.identifier);
        } else {
          field.showRequiredError = false;
        }
      }

      // TODO: Confirm if the fields with Error Effect should restrict user from saving the form
    });
    return blocking;
  };

  /**
   * Flags every shown matrix whose rows break a rule set on it in the builder, clears the flag on the rest, and returns
   * the ids of the flagged ones in form order (ENG-9023). The flag is `invalidInput`, which draws its own message naming
   * the row, apart from Required's: a matrix can be answered and still invalid (ENG-9018 decision 5). A flagged matrix
   * is touched, as a field Save flags for Required is, and not changed.
   */
  const lookForInvalidFields = (): string[] => {
    const invalid: string[] = [];
    form.value?.items?.forEach(field => {
      if (field.type !== FormFieldType.MATRIX) {
        return;
      }
      field.invalidInput = isShownToSave(field) ? matrixRuleViolation(field) : undefined;
      if (field.invalidInput) {
        touchedFields.value.add(field.identifier);
        invalid.push(field.identifier);
      }
    });
    return invalid;
  };

  /**
   * Everything that stops Save: the required fields left unanswered and the fields whose answers break a rule. Save
   * reveals the first of `fieldIds`, which holds both in form order, and announces the two counts apart. A new reason a
   * field stops Save goes in here, never into a second search in `Entry.vue`.
   */
  const lookForBlockingFields = (): { fieldIds: string[]; unanswered: number; invalid: number } => {
    const unanswered = lookForRequiredFields();
    const invalid = lookForInvalidFields();
    const blocking = new Set([...unanswered, ...invalid]);
    const fieldIds = (form.value?.items ?? []).map(field => field.identifier).filter(id => blocking.has(id));
    return { fieldIds, unanswered: unanswered.length, invalid: invalid.length };
  };

  /** Opens or closes a section, taking its fields with it. Every caller says which it wants rather than toggling:
   * only the Expand button is ever closing one, and all the rest are opening a section to reveal a field inside it —
   * which a toggle got wrong whenever that section was open already. */
  const setSectionExpanded = (formField: FormField, expanded: boolean) => {
    formField.sectionExpanded = expanded;
    const sectionFields = formField.options.fields;
    sectionFields?.forEach(field => {
      const foundField = form.value?.items?.find(item => item.identifier === field);
      if (foundField) {
        foundField.expanded = expanded;
      }
    });
  };

  /** Reads the sign history for its dialog. Never rejects: the dialog opens it without awaiting. */
  const fetchEntrySignHistory = async () => {
    if (entry.value?.identifier) {
      entryActivitiesFailed.value = false;
      try {
        entryActivities.value = await EntryService.getEntryActivities(entry.value.identifier);
      } catch {
        entryActivitiesFailed.value = true;
      }
    }
  };

  const entryDisableEdit =
    computed(() => form.value?.hasEntryPermission === false ? translate('entry_edit_no_permission') : undefined);

  return {
    openEntry,
    closeEntrySession,
    setEntryContext,
    clearEntryContext,
    submitEntry,
    entrySaving,
    unsignedEdit,
    downloadDisplayCopy,
    showDiscardChangesDialog,
    cancelDiscardChangesDialog,
    discardEntryChanges,
    switchLangauge,
    lookForRequiredFields,
    lookForBlockingFields,
    changeField,
    isFieldTouched,
    setSectionExpanded,
    getFilesMetadata,
    fileUploads,
    waitForUploads,
    waitingForUploads,
    trackFormStart,
    entrySessionId,
    startedFrom,
    recordStartedFrom,
    selectedCaseIdentifier,
    resolveFn,
    form,
    entry,
    entryLocked,
    getFormLoader,
    effectsSettled,
    formClosedError,
    formClosedMessage,
    anonymousResponsesNotAllowed,
    entryReadRefused,
    openOutcome,
    isEntryPristine,
    openDiscardChangesAlert,
    selectedLanguage,
    entryTokenPreview,
    entryActivities,
    entryActivitiesFailed,
    fetchEntrySignHistory,
    anonymousEntrySubmitError,
    saveConflict,
    isAnonymousEntry,
    isFormPreview,
    closeFormPreview,
    entryDisableEdit,
    multipleEntriesError,
    restoredEntryData,
    resetRestoredEntryData,
    hideRestoredEntryDataMsg
  };
});
